// WHERE A CHAT'S SHOW LIVES — the Day of Show link in a conversation's header (ART-159, C-Mobile).
//
// **The inbox cannot work this out for itself, and that is the whole reason this file exists.** A show chat
// knows its show and its BAND; what it does not know is which shell is rendering it, and the same show has
// two different addresses:
//
//   artist shell  →  /booking/shows/{showId}                        (the band is the MOUNTED one)
//   roster shell  →  /roster/artists/{bandId}/booking/shows/{showId} (the band is in the path)
//
// Deriving it from context is unavailable: the inbox opens from the TOPBAR, which sits outside the artist
// shell's own `BandProvider` — so `useBandContextOrNull()` answers null in BOTH shells and cannot tell them
// apart. That is the same relocation that took `CrewInviteePicker` down (ART-159, §H8), read from the other
// side. So the SHELL says, exactly as it already says where its inbox lives (`InboxButton.route`).
//
// It is a RESOLVER rather than a plain handler because the answer is sometimes "nowhere", and the header
// must not draw a door onto that — an affordance that leads nowhere and a permission that is unreachable
// are the same defect, which is the rule this codebase applies to every picker.

import { useMemo } from "react";
import { useNavigation } from "./hooks/useSession.js";
import { usePhoneLayout } from "./phoneLayout.js";

/** The show a chat is about — what a resolver needs to give it an address. */
export interface ShowLinkTarget {
  showId: string;
  bandId: string;
  /** `YYYY-MM-DD` — the calendar opens on it for a seat whose show surface is the calendar. */
  date?: string | null;
}

/** Resolve a show's address from THIS shell, or undefined when it has none. */
export type ShowLinkResolver = (show: ShowLinkTarget) => string | undefined;

/**
 * Whether this seat gets the Booking page rather than the read-only Calendar: it holds SHOW money at the show
 * grain (`money:full` or `money:per_show`) — Artist/AV, tour managers, management and booking. A plain band
 * member holds only `money:per_person` (their own pay), which makes the server's `money` surface visible for the
 * own-pay chip but is not the Booking page (PO, ART-305: "Band member non AV shouldn't see the booking tab").
 * The ONE rule the desktop nav's Touring menu, the chat's show link and the /booking route guard all read.
 */
export function hasBookingPage(capabilities: Record<string, boolean | undefined> | null | undefined): boolean {
  return !!capabilities?.["money:full"] || !!capabilities?.["money:per_show"];
}

/**
 * The ARTIST shell's rule (ART-270 follow-up, PO): the show's page for THIS viewer, by device and by seat.
 *
 * - **A phone** → that show's Day of Show screen (`/dos?show=…`). Every band seat has the DOS tab, and the DOS
 *   screen opens any show it is handed, past ones included. The Booking page is desktop-only (ART-153), so it
 *   used to be the one place this link sent every phone.
 * - **Desktop, a seat with Booking** (the money surface) → the Booking show preview, as before.
 * - **Desktop, any other seat** → the read-only Calendar, opened on the show's date — the page that seat's nav
 *   calls its schedule.
 *
 * **And a chat for a band that is not the mounted one has no address**, on any device: the show route resolves
 * its band from what is MOUNTED. That case is ordinary, not exotic — the inbox is person-scoped and lists every
 * chat you are in across every band, so somebody on two bands reads both bands' show chats with one band
 * mounted. Navigating there anyway would open the OTHER band's page and find nothing (RLS fences the read to the
 * mounted band), and switching the band is a scorch of the whole query cache, which is not a thing a header link
 * should do behind somebody's back.
 */
export function artistShowLink({
  mountedBandId,
  mobile,
  booking,
}: {
  mountedBandId: string | undefined;
  mobile: boolean;
  booking: boolean;
}): ShowLinkResolver {
  return ({ showId, bandId, date }) => {
    if (!mountedBandId || bandId !== mountedBandId) return undefined;
    if (mobile) return `/dos?show=${showId}&via=link`;
    if (booking) return `/booking/shows/${showId}`;
    return date ? `/calendar?date=${date}` : "/calendar";
  };
}

/** {@link artistShowLink} with the device and seat read here: below `lg` is the phone shell, and the seat's
 *  Booking access comes from the same navigation document the shell's nav renders from (a cached query). */
export function useArtistShowLink(mountedBandId: string | undefined): ShowLinkResolver {
  const mobile = usePhoneLayout();
  const nav = useNavigation(mountedBandId ? `band:${mountedBandId}` : undefined);
  const booking = hasBookingPage(nav.data?.capabilities);
  return useMemo(() => artistShowLink({ mountedBandId, mobile, booking }), [mountedBandId, mobile, booking]);
}

/**
 * The ROSTER shell's rule: the lens carries the artist in the path, so ANY band the chat is for resolves —
 * there is no mounted band to disagree with. **Desktop only**: the roster on a phone has neither a DOS tab nor the
 * Booking cockpit, so there the link is absent.
 *
 * A management seat is only ever in a show chat for a band it reaches, so the route it builds is one the server
 * will serve. Crossing to an artist-shell URL from here would land an org seat on "No band selected", which is the
 * recorded hazard this shape avoids.
 */
export function rosterShowLink({ mobile }: { mobile: boolean }): ShowLinkResolver {
  return ({ showId, bandId }) => (mobile ? undefined : `/roster/artists/${bandId}/booking/shows/${showId}`);
}

/** {@link rosterShowLink} with the device read here. */
export function useRosterShowLink(): ShowLinkResolver {
  const mobile = usePhoneLayout();
  return useMemo(() => rosterShowLink({ mobile }), [mobile]);
}
